import { Loader2, Maximize, Pause, Play, Volume2, VolumeX } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { useLessonVideoUrl } from '@/hooks/use-lessons'

interface VideoPlayerProps {
  lessonId: string
  onProgressUpdate?: (playedSecondsChunk: number, isNewPlayEvent: boolean) => void
  onTimeUpdate?: (currentTime: number) => void
  seekToTime?: number
}

const PROGRESS_INTERVAL_SEC = 10

export function VideoPlayer({
  lessonId,
  onProgressUpdate,
  onTimeUpdate,
  seekToTime,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const { data: videoData } = useLessonVideoUrl(lessonId)
  const videoUrl = (videoData as any)?.url
  const contentType = (videoData as any)?.contentType as string | null
  const isImage = !!contentType?.startsWith('image/')

  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isBuffering, setIsBuffering] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const lastReportedChunk = useRef(0)
  const isNewPlay = useRef(true)

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setCurrentTime(video.currentTime)
    onTimeUpdate?.(video.currentTime)

    // Report progress every PROGRESS_INTERVAL_SEC
    const elapsed = video.currentTime - lastReportedChunk.current
    if (elapsed >= PROGRESS_INTERVAL_SEC) {
      onProgressUpdate?.(elapsed, isNewPlay.current)
      lastReportedChunk.current = video.currentTime
      isNewPlay.current = false
    }
  }, [onTimeUpdate, onProgressUpdate])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play()
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  const toggleFullscreen = () => {
    const video = videoRef.current
    if (!video) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      video.requestFullscreen()
    }
  }

  const handleSeek = (value: number[]) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = value[0]
    setCurrentTime(value[0])
  }

  const handleVolumeChange = (value: number[]) => {
    const video = videoRef.current
    if (!video) return
    video.volume = value[0]
    setVolume(value[0])
  }

  useEffect(() => {
    if (seekToTime !== undefined && videoRef.current) {
      videoRef.current.currentTime = seekToTime
    }
  }, [seekToTime])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (!videoUrl) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-900 text-white">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        <span>コンテンツを読み込み中...</span>
      </div>
    )
  }

  if (isImage) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black">
        <img src={videoUrl} alt="レッスン画像" className="max-h-full max-w-full object-contain" />
      </div>
    )
  }

  return (
    <div className="relative h-full w-full bg-black">
      <video
        ref={videoRef}
        src={videoUrl}
        preload="auto"
        className="h-full w-full"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          if (videoRef.current) setDuration(videoRef.current.duration)
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)}
        onCanPlay={() => setIsBuffering(false)}
        onClick={togglePlay}
      />

      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <Slider
          value={[currentTime]}
          max={duration || 100}
          step={1}
          onValueChange={handleSeek}
          className="mb-2"
        />
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePlay}
              className="text-white hover:text-white"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMute}
              className="text-white hover:text-white"
            >
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
            <div className="w-20">
              <Slider value={[volume]} max={1} step={0.1} onValueChange={handleVolumeChange} />
            </div>
            <span className="text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-white hover:text-white"
          >
            <Maximize className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
